import React, { createContext, useContext, useMemo, ReactNode, useState, useCallback } from 'react'
import { createApiClient, ApiClient } from '../lib/apiClient'

interface ApiClientContextType {
  apiClient: ApiClient
  setThreadId: (threadId: string) => void
}

const ApiClientContext = createContext<ApiClientContextType | null>(null)

export const useApiClient = (): ApiClient => {
  const context = useContext(ApiClientContext)
  if (!context) {
    throw new Error('useApiClient must be used within an ApiClientProvider')
  }
  return context.apiClient
}

export const useApiClientContext = (): ApiClientContextType => {
  const context = useContext(ApiClientContext)
  if (!context) {
    throw new Error('useApiClientContext must be used within an ApiClientProvider')
  }
  return context
}

interface ApiClientProviderProps {
  projectId: string
  threadId?: string
  children: ReactNode
}

export const ApiClientProvider: React.FC<ApiClientProviderProps> = ({ 
  projectId, 
  threadId: initialThreadId,
  children 
}) => {
  const [threadId, setThreadIdState] = useState<string | undefined>(initialThreadId)

  const apiClient = useMemo(() => {
    return createApiClient(projectId, threadId)
  }, [projectId, threadId])

  const setThreadId = useCallback((newThreadId: string) => {
    setThreadIdState(newThreadId)
    apiClient.setThreadId(newThreadId)
  }, [apiClient])

  const contextValue = useMemo(() => ({
    apiClient,
    setThreadId
  }), [apiClient, setThreadId])

  return (
    <ApiClientContext.Provider value={contextValue}>
      {children}
    </ApiClientContext.Provider>
  )
} 